import {
	AfterViewInit,
	Component,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { groupBy } from 'lodash';
import { ActivatedRoute } from '@angular/router';
import { NgFor, KeyValuePipe } from '@angular/common';

/**
 * People who should never appear in the Intermedia call reports, no matter
 * what role/position is stored against them in the database.
 *
 * Names are fuzzy matched (see `namesMatch`), so "Faduma Mahmud" also removes
 * "Faduma BDM Mahmud", "faduma	 mahmud", "Mahmud, Faduma", etc.
 *
 * To exclude someone else, just add their name to this list.
 */
export const EXCLUDED_EMPLOYEES: string[] = ['Faduma Mohamed', 'Frank Eid'];

/**
 * Tokens that show up inside Intermedia display names but are not part of the
 * person's actual name (role tags, department tags, etc). They are stripped
 * before matching, so "Renea BDM Ivanko" still lines up with "Renea Ivanko".
 */
export const NAME_NOISE_TOKENS: string[] = [
	'bdm',
	'csr',
	'mgr',
	'admin',
	'ext',
];

@Component({
	selector: 'app-intermedia-call-reports',
	templateUrl: './intermedia-call-reports.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./intermedia-call-reports.component.scss'],
	imports: [NgFor, KeyValuePipe],
})
export class IntermediaCallReportsComponent implements OnInit {
	public EmployeesList: any;
	public EmployeesCallData: any;
	public employeeType: any;
	public noAverage: boolean;
	public colspan: number;
	public notesLeft: any;

	/** Cache of Intermedia name -> database name, so we only match once per name. */
	private nameMatchCache = new Map<string, string | null>();

	constructor(
		private apiService: LegacyApiService,
		private activatedRoute: ActivatedRoute
	) {}

	async ngOnInit() {
		this.activatedRoute?.queryParams?.subscribe((queryParams) => {
			this.employeeType = queryParams?.['employeeType'];
			this.noAverage = queryParams?.['noAverage'];
			this.colspan = this.noAverage ? 2 : 3;
		});

		const allEmployees = (await this.getEmployeesForUniteCalls(
			this.employeeType
		)) as string[];

		// Drop anyone on the exclusion list before anything else is built.
		this.EmployeesList = (allEmployees || []).filter(
			(employee) => !this.isExcluded(employee)
		);

		this.notesLeft = await this.getFilesTouched(this.employeeType);
		this.nameMatchCache.clear();

		this.apiService.getUniteAuth().subscribe((res: any) => {
			const token = res.access_token;
			this.apiService.getUniteCalls(token).subscribe((res: any) => {
				const calls = res.calls;
				calls.forEach((call, i, arr) => {
					const name =
						call.direction === 'inbound' ? call.to.name : call.from.name;
					return (arr[i] = { ...call, ...{ name } });
				});

				// Match each Intermedia name back to the database name, so
				// "Renea BDM Ivanko" is reported under "Renea Ivanko".
				const matchedCalls = [];
				const unmatchedNames = new Set<string>();
				calls.forEach((call) => {
					const employeeName = this.resolveEmployeeName(call.name);
					if (employeeName) {
						matchedCalls.push({ ...call, employeeName });
					} else if (call.name) {
						unmatchedNames.add(call.name);
					}
				});

				if (unmatchedNames.size) {
					// Handy for spotting future name mismatches / new starters.
					console.warn(
						'[Intermedia call reports] No employee match for:',
						Array.from(unmatchedNames)
					);
				}

				this.EmployeesCallData = groupBy(matchedCalls, 'employeeName');

				Object.keys(this.EmployeesCallData).forEach((key, index) => {
					const inboundCalls = this.EmployeesCallData[key].filter(
						(call) => call.direction === 'inbound'
					);
					const outboundCalls = this.EmployeesCallData[key].filter(
						(call) => call.direction === 'outbound'
					);
					let filesTouched: number;
					(this.notesLeft || []).forEach((item) => {
						if (this.namesMatch(item['created_by'], key)) {
							filesTouched = item['Notes Left'];
						}
					});
					const inboundTotalCallTime = inboundCalls
						.map((callList) => callList.duration)
						.reduce((acc, val) => acc + val, 0);
					const inboundTotalCallTimeFormatted =
						this.secondsToTime(inboundTotalCallTime);
					const inboundAvgCallTime =
						inboundTotalCallTime / inboundCalls.length || 0;
					const inboundAvgCallTimeFormatted =
						this.secondsToTime(inboundAvgCallTime);
					const inboundTotalCalls = inboundCalls.length;
					const outboundTotalCallTime = outboundCalls
						.map((callList) => callList.duration)
						.reduce((acc, val) => acc + val, 0);
					const outboundTotalCallTimeFormatted = this.secondsToTime(
						outboundTotalCallTime
					);
					const outboundAvgCallTime =
						outboundTotalCallTime / outboundCalls.length || 0;
					const outboundAvgCallTimeFormatted =
						this.secondsToTime(outboundAvgCallTime);
					const outboundTotalCalls = outboundCalls.length;
					const totalCalls = outboundTotalCalls + inboundTotalCalls;
					const totalCallTime = outboundTotalCallTime + inboundTotalCallTime;
					const totalCallTimeFormatted = this.secondsToTime(totalCallTime);
					this.EmployeesCallData[key] = {
						inboundTotalCalls,
						inboundAvgCallTimeFormatted,
						inboundTotalCallTimeFormatted,
						outboundTotalCalls,
						outboundAvgCallTimeFormatted,
						outboundTotalCallTimeFormatted,
						totalCalls,
						totalCallTimeFormatted,
						filesTouched,
					};
				});
			});
		});
	}

	// ---------------------------------------------------------------------
	// Name matching
	// ---------------------------------------------------------------------

	/** True when a name is on the exclusion list (fuzzy matched). */
	isExcluded(name: string): boolean {
		return EXCLUDED_EMPLOYEES.some((excluded) =>
			this.namesMatch(excluded, name)
		);
	}

	/**
	 * Turns a display name into comparable tokens: lower cased, punctuation
	 * removed, role tags dropped. "Ivanko, Renea (BDM)" -> ['ivanko', 'renea'].
	 */
	private normalizeName(name: string): string[] {
		return String(name ?? '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, ' ')
			.trim()
			.split(' ')
			.filter((token) => token && NAME_NOISE_TOKENS.indexOf(token) === -1);
	}

	/**
	 * Two name parts match if they are identical, one is an initial of the
	 * other ("r" / "renea"), or they are within one typo of each other
	 * ("mahmud" / "mahmoud").
	 */
	private tokensMatch(a: string, b: string): boolean {
		if (a === b) {
			return true;
		}
		if ((a.length === 1 || b.length === 1) && a[0] === b[0]) {
			return true;
		}
		if (Math.min(a.length, b.length) < 4) {
			return false;
		}
		if (Math.abs(a.length - b.length) > 1) {
			return false;
		}
		// Single insert / delete / substitution allowed.
		let i = 0;
		let j = 0;
		let edits = 0;
		while (i < a.length && j < b.length) {
			if (a[i] === b[j]) {
				i++;
				j++;
				continue;
			}
			if (++edits > 1) {
				return false;
			}
			if (a.length === b.length) {
				i++;
				j++;
			} else if (a.length > b.length) {
				i++;
			} else {
				j++;
			}
		}
		edits += a.length - i + (b.length - j);
		return edits <= 1;
	}

	/**
	 * Score of how well two names match, or -1 when they do not match at all.
	 * Every part of the shorter name must appear in the longer one, which is
	 * what lets "Renea Ivanko" match "Renea BDM Ivanko" but not "Renea Smith".
	 * Single word names must match a single word name exactly, so a lone
	 * "Renea" is never silently attached to the wrong person.
	 */
	private nameMatchScore(nameA: string, nameB: string): number {
		const a = this.normalizeName(nameA);
		const b = this.normalizeName(nameB);
		if (!a.length || !b.length) {
			return -1;
		}
		const short = a.length <= b.length ? a : b;
		const long = a.length <= b.length ? b : a;
		if (short.length === 1 && long.length !== 1) {
			return -1;
		}
		const pool = long.slice();
		let score = 0;
		for (const token of short) {
			const exact = pool.indexOf(token);
			const index =
				exact !== -1
					? exact
					: pool.findIndex((candidate) => this.tokensMatch(candidate, token));
			if (index === -1) {
				return -1;
			}
			score += exact !== -1 ? 2 : 1;
			pool.splice(index, 1);
		}
		// Prefer the tightest match when two employees both qualify.
		return score - (long.length - short.length);
	}

	/** True when two names refer to the same person. */
	namesMatch(nameA: string, nameB: string): boolean {
		return this.nameMatchScore(nameA, nameB) >= 0;
	}

	/**
	 * Maps an Intermedia caller name onto the matching database name.
	 * Returns null when there is no match, when the person is excluded, or
	 * when the name matches two different employees equally well.
	 */
	private resolveEmployeeName(callName: string): string | null {
		const key = String(callName ?? '').trim();
		if (!key) {
			return null;
		}
		if (this.nameMatchCache.has(key)) {
			return this.nameMatchCache.get(key);
		}

		let best: string | null = null;
		let bestScore = -1;
		let tied = false;

		(this.EmployeesList || []).forEach((employee: string) => {
			const score = this.nameMatchScore(employee, key);
			if (score < 0) {
				return;
			}
			if (score > bestScore) {
				best = employee;
				bestScore = score;
				tied = false;
			} else if (score === bestScore && employee !== best) {
				tied = true;
			}
		});

		if (tied) {
			console.warn('[Intermedia call reports] Ambiguous name, skipped:', key);
			best = null;
		}
		if (best && this.isExcluded(best)) {
			best = null;
		}

		this.nameMatchCache.set(key, best);
		return best;
	}

	// ---------------------------------------------------------------------

	secondsToTime(seconds: number) {
		let dateObj = new Date(seconds * 1000);
		let hours = dateObj.getUTCHours();
		let minutes = dateObj.getUTCMinutes();
		seconds = dateObj.getSeconds();

		return (
			hours.toString().padStart(2, '0') +
			':' +
			minutes.toString().padStart(2, '0') +
			':' +
			seconds.toString().padStart(2, '0')
		);
	}
	getEmployeesForUniteCalls(et) {
		return new Promise((resolve, reject) => {
			this.apiService.getEmployeesForUniteCalls(et).subscribe((res: any) => {
				resolve(res?.data?.map((emp) => emp.full_name));
			});
		});
	}
	getFilesTouched(et) {
		return new Promise((resolve, reject) => {
			this.apiService.getFilesTouched(et).subscribe((res: any) => {
				resolve(res.data);
			});
		});
	}
}
